import IframeResizer from '@iframe-resizer/react';
import isNil from 'lodash/isNil';
import { useState } from 'react';

import {
  injectCountrySlugAndLanguageSlugIntoUrl,
  isValidBreitlingDomain,
} from '@/utils/urlUtils';

import { Container } from '@/components/Container';
import { Loader } from '@/components/Loader';
import { Visibility } from '@/components/Visibility';
import type { ContentfulIframeFragment } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { IFRAME_RESIZER_LICENSE, PLACEHOLDER_SF_ID_SLUG } from '@/lib/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { BreakpointsKeys } from '@/theme/types';

type IframeMaxWidth = Exclude<BreakpointsKeys, 'xs'> | 'full';
export type IframeProps = Omit<ContentfulIframeFragment, 'maxWidth' | 'sys'> & {
  maxWidth?: IframeMaxWidth;
};

export const Iframe = ({
  hasScrollbar,
  height,
  internalName,
  maxWidth,
  url,
}: IframeProps) => {
  const { country, locale } = useRegionProvider();
  const {
    snapshot: { isAuthenticated, isInitializing, user },
  } = useUser();
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  if (!url || !isValidBreitlingDomain(url)) {
    return null;
  }
  const isLoadingUser = isNil(isAuthenticated) || isInitializing;

  const needWaitUser = url.includes(PLACEHOLDER_SF_ID_SLUG);

  if (needWaitUser && isLoadingUser) {
    return <Loader data-testid='iframeLoader' height={400}></Loader>;
  }
  const salesForceId = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.salesForceId,
  );

  const src = injectCountrySlugAndLanguageSlugIntoUrl({
    url,
    countrySlug: country.slug,
    languageSlug: locale.slug,
    salesForceId,
  });

  const iframeUrl = new URL(src);
  const iframeParams = new URLSearchParams(iframeUrl.search);

  const routeParams = new URLSearchParams(router.query as any);

  routeParams.forEach((value, key) => {
    if (key !== 'slug') {
      iframeParams.set(key, value);
    }
  });

  iframeUrl.search = iframeParams.toString();

  const IframeTemplate = (
    <>
      {loading && <Loader data-testid='iframeLoader' height={400}></Loader>}
      <Visibility xsHidden={loading}>
        {!Number(height) ? (
          <IframeResizer
            data-test-id='iframeResizer'
            license={IFRAME_RESIZER_LICENSE}
            log={false}
            onLoad={() => setLoading(false)}
            onReady={() => setLoading(false)}
            scrolling='omit'
            src={iframeUrl.toString()}
            style={{ width: '100%', height: '1024px' }}
            title={internalName ?? ''}
          />
        ) : (
          <iframe
            data-testid='iframeSection'
            height={height ?? 0}
            onLoad={() => setLoading(false)}
            scrolling={hasScrollbar ? 'auto' : 'no'}
            src={iframeUrl.toString()}
            title={internalName ?? ''}
            width='100%'
          ></iframe>
        )}
      </Visibility>
    </>
  );
  if (maxWidth === 'full') return IframeTemplate;
  return <Container maxWidth={maxWidth}>{IframeTemplate}</Container>;
};

Iframe.displayName = 'Iframe';
